import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Profile, InstructorProfile, StudentProfile } from '@/types'

interface AuthContextValue {
  session: Session | null
  user: User | null
  profile: Profile | null
  instructorProfile: InstructorProfile | null
  studentProfile: StudentProfile | null
  // true only for a school_admin who OWNS a school still gated by the required
  // business-profile wizard (migration 080) — server-authoritative, same role this plays
  // for instructorProfile.onboarding_completed_at below. null while unknown/not applicable.
  schoolProfileRequired: boolean | null
  // True during initial session resolution on page load
  loading: boolean
  // True while the profile row is being fetched after session is known
  profileLoading: boolean
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [instructorProfile, setInstructorProfile] = useState<InstructorProfile | null>(null)
  const [studentProfile, setStudentProfile] = useState<StudentProfile | null>(null)
  const [schoolProfileRequired, setSchoolProfileRequired] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Tracks whichever fetchProfile call is the MOST RECENTLY STARTED one, synchronously
  // (a ref, not state — state updates land too late to guard the very next line). A fast
  // sign-out-then-sign-in-as-someone-else cycle (or the initial getSession() call on page
  // load racing a near-simultaneous SIGNED_IN event) can have two fetchProfile calls for
  // two DIFFERENT user ids in flight at once; without this guard, whichever one's network
  // round trip happens to resolve LAST wins the setProfile() call regardless of which one
  // was actually requested last — observed as "logged in as admin, signed out, signed in
  // as instructor, sometimes still see the admin portal" (a real, reported bug: the
  // leftover slower admin-profile fetch finishing after the instructor one, silently
  // overwriting the correct profile). Every setProfile/setInstructorProfile/
  // setStudentProfile call below is skipped if a newer call has since superseded this one.
  const latestRequestedUserId = useRef<string | null>(null)

  const fetchProfile = useCallback(async (userId: string) => {
    latestRequestedUserId.current = userId
    setProfileLoading(true)
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (latestRequestedUserId.current !== userId) return // superseded — ignore this result

      if (error) {
        // PGRST116 = no rows found — profile may not exist yet (e.g. just after Google OAuth)
        if (error.code !== 'PGRST116') {
          console.error('[AuthContext] Profile fetch error:', error.message)
        }
        setProfile(null)
        setInstructorProfile(null)
        setStudentProfile(null)
        setSchoolProfileRequired(null)
      } else {
        const fetchedProfile = data as Profile
        setProfile(fetchedProfile)

        if (fetchedProfile.primary_role === 'instructor') {
          // Fetch instructor_profiles only for instructors.
          // This is the server-authoritative onboarding completion status.
          const { data: ip, error: ipErr } = await supabase
            .from('instructor_profiles')
            .select('*')
            .eq('user_id', userId)
            .maybeSingle()

          if (latestRequestedUserId.current !== userId) return

          if (ipErr) {
            console.error('[AuthContext] instructor_profiles fetch error:', ipErr.message)
            setInstructorProfile(null)
          } else {
            setInstructorProfile(ip as InstructorProfile | null)
          }
          setStudentProfile(null)

        } else if (fetchedProfile.primary_role === 'student') {
          // Fetch student_profiles for students.
          // Contains G1 licence data and date of birth.
          const { data: sp, error: spErr } = await supabase
            .from('student_profiles')
            .select('*')
            .eq('user_id', userId)
            .maybeSingle()

          if (latestRequestedUserId.current !== userId) return

          if (spErr) {
            console.error('[AuthContext] student_profiles fetch error:', spErr.message)
            setStudentProfile(null)
          } else {
            setStudentProfile(sp as StudentProfile | null)
          }
          setInstructorProfile(null)

        } else if (fetchedProfile.primary_role === 'school_admin') {
          // Server-authoritative gate for the required business-profile wizard
          // (migration 080): a school whose OWNER membership points at a
          // requires_profile_wizard=true, profile_completed_at=null row is still gated.
          // Pre-existing schools are backfilled requires_profile_wizard=false in that
          // migration, so this is never true for them regardless of empty fields.
          const { data: owned, error: ownedErr } = await supabase
            .from('school_memberships')
            .select('schools(requires_profile_wizard, profile_completed_at)')
            .eq('user_id', userId)
            .eq('role', 'OWNER')
            .eq('status', 'active')

          if (latestRequestedUserId.current !== userId) return

          if (ownedErr) {
            console.error('[AuthContext] school ownership fetch error:', ownedErr.message)
            setSchoolProfileRequired(null)
          } else {
            const required = (owned ?? []).some((row: any) =>
              row.schools?.requires_profile_wizard === true && row.schools?.profile_completed_at == null
            )
            setSchoolProfileRequired(required)
          }
          setInstructorProfile(null)
          setStudentProfile(null)

        } else {
          setInstructorProfile(null)
          setStudentProfile(null)
          setSchoolProfileRequired(null)
        }
      }
    } finally {
      if (latestRequestedUserId.current === userId) setProfileLoading(false)
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id)
  }, [user, fetchProfile])

  useEffect(() => {
    // Get the initial session synchronously from storage
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession)
      setUser(initialSession?.user ?? null)
      if (initialSession?.user) {
        fetchProfile(initialSession.user.id).finally(() => setLoading(false))
      } else {
        setLoading(false)
      }
    })

    // Subscribe to all future auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession)
        setUser(newSession?.user ?? null)

        if (event === 'SIGNED_OUT') {
          // Also invalidates any fetchProfile call still in flight for the account that
          // just signed out, so it can never land after this point (see
          // latestRequestedUserId's own comment above).
          latestRequestedUserId.current = null
          setProfile(null)
          setInstructorProfile(null)
          setStudentProfile(null)
          setSchoolProfileRequired(null)
          return
        }

        if (newSession?.user) {
          await fetchProfile(newSession.user.id)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [fetchProfile])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    // State is cleared by onAuthStateChange SIGNED_OUT event above
  }, [])

  return (
    <AuthContext.Provider
      value={{ session, user, profile, instructorProfile, studentProfile, schoolProfileRequired, loading, profileLoading, signOut, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
